import { useGetBranding } from '@sanlam-glacier/app-common/modules/theme';
import { useConfig } from '@sanlam-glacier/app-common/providers';
import { clsx } from 'clsx';
import styles from './login-unavailable-page.module.scss';
import {
  Button,
  Divider,
  Icon,
  IconButton,
  ICONS_ENUM,
  Typography
} from '@sanlam-glacier/app-common/components';
import { displayToast } from '@sanlam-glacier/app-common/modules/toasts';
import { Theme } from '../../atoms/theme/Theme';
import { useEffect, useState } from 'react';
import { useAuth } from 'auth/auth-provider/AuthProvider';
import { CORRELATION_ID_STORAGE_KEY, HOST_BRAND } from 'auth/auth-helpers/auth-helper';
import { RedirectsConfig } from '@sanlam-glacier/app-common/types/env/RedirectsConfig.js';

export const LoginUnavailable = () => {
  const authApi = useAuth();
  const { afConnectSupportEmail, redirects, theme } = useConfig();
  const { somethingWentWrong, glacierLogoColor, brandLogoColor } = useGetBranding(theme);
  const isUserFPC = localStorage.getItem(HOST_BRAND) === 'afglc_fpc';

  const [referenceId] = useState(
    () => sessionStorage.getItem(CORRELATION_ID_STORAGE_KEY) ?? 'Undefined'
  );

  useEffect(() => {
    sessionStorage.removeItem(CORRELATION_ID_STORAGE_KEY);
  }, []);

  const handleCopy = (value: string, type: 'email' | 'referenceId') => {
    navigator.clipboard.writeText(value);
    const toastConfig = {
      email: {
        toastId: 'supportEmailCopied',
        message: 'Support email address copied successfully.'
      },
      referenceId: {
        toastId: 'referenceIdCopied',
        message: 'Reference Id copied successfully.'
      }
    };
    displayToast({
      toastId: toastConfig[type].toastId,
      title: 'Copy successful',
      message: toastConfig[type].message,
      status: 'success'
    });
  };

  const logIn = async () => {
    await authApi.login({ authSuccessRedirect: location.pathname + location.search });
  };

  const handleContactSupport = () => {
    if (isUserFPC) {
      const redirectUrls = redirects as RedirectsConfig;
      open(redirectUrls.AlexForbesAdvisorConnectSupport, '_blank');
    } else {
      location.href = `mailto:${afConnectSupportEmail}`;
    }
  };

  return (
    <Theme variant='secondary'>
      <div className={styles.logoContainer}>
        <img alt='Sanlam Glacier' className={styles.topBarIcon} src={glacierLogoColor} />
        {brandLogoColor && (
          <>
            <Divider position={Divider.POSITION.VERTICAL} />
            <img alt='Brand' className={styles.brandLogo} src={brandLogoColor} />
          </>
        )}
      </div>
      <div className={styles.loginUnavailable}>
        <img
          className={clsx(styles.loginUnavailableIllustration)}
          src={somethingWentWrong}
          alt='Error illustration'
        />
        <div className={styles.loginUnavailableCopy}>
          <Typography
            variant={Typography.VARIANT.H1}
            weight={Typography.WEIGHT.BOLD}
            color={Typography.COLOR.DEFAULT}
            className={styles.heading}
          >
            Glacier Hub login is temporarily unavailable.
          </Typography>
          <Typography variant={Typography.VARIANT.B1} color={Typography.COLOR.TERTIARY}>
            {`Please try again shortly. If the problem persists, contact first line support${isUserFPC ? ' via the Advisor Connect Support Site' : ''}.`}
          </Typography>

          <div className={styles.callToActionSection}>
            <div className={styles.actionButtons}>
              <Button
                onClick={logIn}
                size={Button.SIZE.SMALL}
                startIcon={<Icon icon={ICONS_ENUM.NAVIGATION_REFRESH} />}
                variant={Button.VARIANT.SECONDARY}
              >
                Try again
              </Button>
              <Button
                onClick={handleContactSupport}
                size={Button.SIZE.SMALL}
                startIcon={<Icon icon={!isUserFPC && ICONS_ENUM.COMMUNICATION_EMAIL} />}
                endIcon={<Icon icon={isUserFPC && ICONS_ENUM.ACTION_OPEN_IN_NEW} />}
                variant={Button.VARIANT.PRIMARY}
              >
                {isUserFPC ? 'Advisor Connect Support Site' : 'Contact support'}
              </Button>
            </div>
            {!isUserFPC && (
              <Theme variant='primary'>
                <div className={styles.textWithCopyAction}>
                  <Typography variant={Typography.VARIANT.B4} color={Typography.COLOR.TERTIARY}>
                    Or email us directly at{' '}
                    <span className={styles.bold}>{afConnectSupportEmail as string}</span>
                  </Typography>
                  <IconButton
                    size={IconButton.SIZE.XX_SMALL}
                    variant={IconButton.VARIANT.TERTIARY}
                    onClick={() => handleCopy(afConnectSupportEmail as string, 'email')}
                  >
                    <Icon icon={ICONS_ENUM.ACTION_2_COPY} />
                  </IconButton>
                </div>
              </Theme>
            )}
          </div>
          {referenceId !== 'Undefined' && (
            <div className={styles.referenceIdSection}>
              <Typography
                variant={Typography.VARIANT.O2}
                color={Typography.COLOR.TERTIARY}
                weight={Typography.WEIGHT.BOLD}
              >
                reference id
              </Typography>
              <Theme variant='primary'>
                <div className={styles.textWithCopyAction}>
                  <Typography variant={Typography.VARIANT.B4} color={Typography.COLOR.DEFAULT}>
                    {referenceId}
                  </Typography>
                  <IconButton
                    size={IconButton.SIZE.XX_SMALL}
                    variant={IconButton.VARIANT.TERTIARY}
                    onClick={() => handleCopy(referenceId, 'referenceId')}
                  >
                    <Icon icon={ICONS_ENUM.ACTION_2_COPY} />
                  </IconButton>
                </div>
              </Theme>

              <Typography variant={Typography.VARIANT.B4} color={Typography.COLOR.TERTIARY}>
                Share this with support to help resolve your issue
              </Typography>
            </div>
          )}
        </div>
      </div>
    </Theme>
  );
};
